import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import API_BASE from "../config";
import { extractErrorMessage } from "../utils/apiError";
import s from "./RoomPicker.module.css";

const ROOM_TYPES = [
  { value: "STANDARD",   label: "Standard",   pill: null },
  { value: "LAB",        label: "Lab",        pill: s.optionTypePillLab },
  { value: "MUSIC",      label: "Music",      pill: s.optionTypePillMusic },
  { value: "GYM",        label: "Gym",        pill: s.optionTypePillGym },
  { value: "LIBRARY",    label: "Library",    pill: s.optionTypePillLibrary },
  { value: "AUDITORIUM", label: "Auditorium", pill: null },
];

function authHeaders() {
  let user = null;
  try { user = JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
  catch { /* ignore */ }
  return {
    "Content-Type": "application/json",
    "X-User-Role":  user?.role     || "",
    "X-Username":   user?.username || "",
  };
}

function metaLine(room) {
  const bits = [];
  if (room.building) bits.push(room.building);
  if (room.floor)    bits.push(`Floor ${room.floor}`);
  if (room.capacity != null) bits.push(`${room.capacity} seats`);
  return bits.join(" · ");
}

function pillClassFor(roomType) {
  const t = ROOM_TYPES.find(rt => rt.value === (roomType || "").toUpperCase());
  return t?.pill || null;
}

/**
 * Reusable Room dropdown + inline-create. Anchored to its trigger via a
 * portaled, fixed-position panel so the dropdown isn't clipped by
 * overflow:hidden parents (table wrappers, modals, etc).
 *
 * Props:
 *   value        number | null  — current roomId
 *   onChange     fn(roomId | null)
 *   rooms        Array<Room>    — optional preload from parent; falls back
 *                                 to fetching /api/rooms on first open
 *   onRoomsChanged fn(rooms)    — invoked after a successful create so
 *                                 the parent can refresh its own copy
 *   placeholder  string         — text when no value selected
 *   required     boolean
 *   disabled     boolean
 *   allowCreate  boolean (default true) — show the "+ Add new room" footer
 *   allowClear   boolean (default true) — show the × clear button
 */
export default function RoomPicker({
  value,
  onChange,
  rooms: propRooms,
  onRoomsChanged,
  placeholder = "Select a room…",
  required = false,
  disabled = false,
  allowCreate = true,
  allowClear  = true,
}) {
  const triggerRef = useRef(null);
  const panelRef   = useRef(null);
  const searchRef  = useRef(null);
  const [coords, setCoords] = useState(null);
  const open = !!coords;

  const [internalRooms, setInternalRooms] = useState(propRooms || []);
  const rooms = propRooms || internalRooms;
  const [search, setSearch] = useState("");

  // Inline create form state
  const [showAdd, setShowAdd]     = useState(false);
  const [addBusy, setAddBusy]     = useState(false);
  const [addError, setAddError]   = useState("");
  const [addForm, setAddForm]     = useState({
    name: "", building: "", floor: "", capacity: "", roomType: "STANDARD",
  });

  // Currently-selected room (for label rendering on the trigger).
  const selectedRoom = useMemo(
    () => rooms.find(r => String(r.id) === String(value)) || null,
    [rooms, value]
  );

  // ── Fetch rooms on first open if parent didn't preload ─────────────
  async function ensureRooms() {
    if (propRooms || internalRooms.length > 0) return;
    try {
      const res = await fetch(`${API_BASE}/api/rooms`);
      if (!res.ok) return;
      const list = await res.json();
      setInternalRooms(Array.isArray(list) ? list : []);
    } catch { /* silent — empty state is acceptable */ }
  }

  function placeAndOpen() {
    if (disabled) return;
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const desired = { top: r.bottom + 6, left: r.left, width: r.width };
    // Vertical-flip when there isn't enough room below the trigger. Anchor
    // the flipped panel by its BOTTOM edge (just above the trigger) rather
    // than computing a top from an estimated height — the real panel is
    // often much shorter (few rooms), which left it floating far above.
    const estHeight = 380;
    const spaceBelow = window.innerHeight - r.bottom - 12;
    if (spaceBelow < estHeight && r.top > spaceBelow) {
      desired.top = null;
      desired.bottom = window.innerHeight - r.top + 6;
    }
    setCoords(desired);
    setSearch("");
    setShowAdd(false);
    setAddError("");
    ensureRooms();
    setTimeout(() => searchRef.current?.focus(), 30);
  }

  function close() {
    setCoords(null);
    setShowAdd(false);
  }

  // Outside click + ESC + scroll-close, mirroring the kebab pattern from
  // Pass 2. Scroll-close keeps the anchored coords from drifting.
  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (triggerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target))   return;
      close();
    }
    function onKey(e) { if (e.key === "Escape") close(); }
    function onScroll() { close(); }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  // ── Filter ──────────────────────────────────────────────────────────
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rooms;
    return rooms.filter(r => {
      const hay = [r.name, r.building, r.floor, r.roomType]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rooms, search]);

  // ── Create flow ─────────────────────────────────────────────────────
  function resetAddForm() {
    setAddForm({ name: "", building: "", floor: "", capacity: "", roomType: "STANDARD" });
    setAddError("");
  }

  async function saveNew() {
    const name = addForm.name.trim();
    if (!name) { setAddError("Name is required."); return; }
    if (addForm.capacity !== "" && Number.isNaN(Number(addForm.capacity))) {
      setAddError("Capacity must be a number."); return;
    }
    setAddBusy(true); setAddError("");
    try {
      const payload = {
        name,
        building: addForm.building.trim() || null,
        floor:    addForm.floor.trim()    || null,
        capacity: addForm.capacity === "" ? null : Number(addForm.capacity),
        roomType: addForm.roomType || null,
      };
      const res = await fetch(`${API_BASE}/api/rooms`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Request failed (${res.status})`));
      const created = await res.json();
      // Refresh local list (or notify parent) and select the new room.
      const next = [...rooms, created].sort(
        (a, b) => (a.name || "").localeCompare(b.name || "")
      );
      if (propRooms) {
        onRoomsChanged && onRoomsChanged(next);
      } else {
        setInternalRooms(next);
      }
      onChange(created.id);
      resetAddForm();
      setShowAdd(false);
      close();
    } catch (e) {
      setAddError(e.message || "Failed to save");
    } finally {
      setAddBusy(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────
  const triggerClasses = [
    s.trigger,
    open && s.triggerOpen,
    disabled && s.triggerDisabled,
  ].filter(Boolean).join(" ");

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClasses}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-required={required}
        disabled={disabled}
        onClick={() => (open ? close() : placeAndOpen())}>
        <span className={s.triggerLabel}>
          {selectedRoom ? (
            <>
              <span className={s.triggerName}>{selectedRoom.name}</span>
              {metaLine(selectedRoom) && (
                <span className={s.triggerMeta}>{metaLine(selectedRoom)}</span>
              )}
            </>
          ) : (
            <span className={s.triggerPlaceholder}>{placeholder}</span>
          )}
        </span>
        {allowClear && selectedRoom && !disabled && (
          <span
            role="button"
            tabIndex={-1}
            className={s.clearBtn}
            aria-label="Clear room"
            onClick={(e) => { e.stopPropagation(); onChange(null); }}>
            ×
          </span>
        )}
        <span className={s.triggerCaret} aria-hidden="true">▾</span>
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          className={s.panel}
          role="dialog"
          aria-label="Room picker"
          style={{
            top:    coords.top    ?? undefined,
            bottom: coords.bottom ?? undefined,
            left:   coords.left,
            minWidth: Math.max(280, coords.width),
          }}>
          <input
            ref={searchRef}
            className={s.search}
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search rooms by name, building, type…"
            aria-label="Search rooms"
          />

          {visible.length === 0 ? (
            <div className={s.empty}>
              {rooms.length === 0
                ? "No rooms yet."
                : "No rooms match the search."}
            </div>
          ) : (
            <ul className={s.list} role="listbox">
              {visible.map(r => {
                const isSelected = String(r.id) === String(value);
                const pill = pillClassFor(r.roomType);
                return (
                  <li key={r.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={`${s.option} ${isSelected ? s.optionSelected : ""}`}
                      onClick={() => { onChange(r.id); close(); }}>
                      <span className={s.optionTick}>{isSelected ? "✓" : ""}</span>
                      <span className={s.optionMain}>
                        <span className={s.optionName}>{r.name}</span>
                        {metaLine(r) && <span className={s.optionMeta}>{metaLine(r)}</span>}
                      </span>
                      {r.roomType && (
                        <span className={`${s.optionTypePill} ${pill || ""}`}>
                          {r.roomType}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {allowCreate && (
            <div className={s.foot}>
              {!showAdd ? (
                <button
                  type="button"
                  className={s.addBtn}
                  onClick={() => { setShowAdd(true); setAddError(""); }}>
                  <span className={s.addPlus}>+</span>
                  <span>Add new room</span>
                </button>
              ) : (
                <div className={s.form} role="group" aria-label="Add new room">
                  <div className={s.formTitle}>New room</div>
                  {addError && <div className={s.formError}>{addError}</div>}
                  <div className={s.formGrid}>
                    <div className={`${s.formField} ${s.formGridFull}`}>
                      <label className={s.formLabel}>
                        Name<span className={s.formReq}> *</span>
                      </label>
                      <input
                        className={s.formInput}
                        type="text"
                        value={addForm.name}
                        onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="Rm 201"
                        autoFocus
                        disabled={addBusy}
                      />
                    </div>
                    <div className={s.formField}>
                      <label className={s.formLabel}>Building</label>
                      <input
                        className={s.formInput}
                        type="text"
                        value={addForm.building}
                        onChange={e => setAddForm(f => ({ ...f, building: e.target.value }))}
                        placeholder="Main"
                        disabled={addBusy}
                      />
                    </div>
                    <div className={s.formField}>
                      <label className={s.formLabel}>Floor</label>
                      <input
                        className={s.formInput}
                        type="text"
                        value={addForm.floor}
                        onChange={e => setAddForm(f => ({ ...f, floor: e.target.value }))}
                        placeholder="2"
                        disabled={addBusy}
                      />
                    </div>
                    <div className={s.formField}>
                      <label className={s.formLabel}>Capacity</label>
                      <input
                        className={s.formInput}
                        type="number"
                        min="0"
                        value={addForm.capacity}
                        onChange={e => setAddForm(f => ({ ...f, capacity: e.target.value }))}
                        placeholder="40"
                        disabled={addBusy}
                      />
                    </div>
                    <div className={s.formField}>
                      <label className={s.formLabel}>Type</label>
                      <select
                        className={s.formSelect}
                        value={addForm.roomType}
                        onChange={e => setAddForm(f => ({ ...f, roomType: e.target.value }))}
                        disabled={addBusy}>
                        {ROOM_TYPES.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className={s.formActions}>
                    <button
                      type="button"
                      className={s.btnGhost}
                      onClick={() => { setShowAdd(false); resetAddForm(); }}
                      disabled={addBusy}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={s.btnPrimary}
                      onClick={saveNew}
                      disabled={addBusy}>
                      {addBusy ? "Saving…" : "Save room"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}
